/**
 * MobileCarousel Component
 * Mobile-specific implementation of the BrandsCarousel with a 2-column grid layout
 * when useAlternativeLayout is true, otherwise uses a vertical layout
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Typography, Skeleton, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import BrandCard from './BrandCard';

// Import styles directly from their source files
import { commonContainerStyles, commonSeoDescriptionStyles, commonTitleStyles } from '../styles/common';
import { 
  mobileContainerStyles, 
  mobileCarouselContainerStyles,
  mobileGridStyles,
  mobileTitleStyles,
  mobileVerticalGridStyles,
  mobileVerticalBrandCardStyles
} from '../styles/mobile';

// --- Framer Motion Variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1, // Stagger animation for each card
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};
// --- End Framer Motion Variants ---

const MobileCarousel = ({ 
  brands, 
  showTitle = true, 
  useAlternativeLayout = true,
  currentBrandId = null
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate API loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000); // Simulate 1 second loading time
    
    return () => clearTimeout(timer); // Cleanup timer on unmount
  }, []);
  
  // Determine which grid style to use based on useAlternativeLayout prop
  const gridStyles = useAlternativeLayout ? mobileGridStyles : mobileVerticalGridStyles;
  
  // --- Skeleton Loader ---
  const renderSkeletons = () => {
    const skeletonCount = 6; // Show 6 placeholders
    const cardHeight = useAlternativeLayout ? '130px' : 'auto'; // Match card height based on layout
    const cardMarginBottom = useAlternativeLayout ? 2 : 1; // Match grid gap

    return (
      <Box sx={gridStyles}>
        {[...Array(skeletonCount)].map((_, index) => (
          <Grid item key={index} sx={useAlternativeLayout ? {} : mobileVerticalBrandCardStyles} >
            <Skeleton 
              variant="rectangular" 
              width="100%" 
              height={cardHeight} 
              sx={{ borderRadius: 1, mb: cardMarginBottom }}
              animation="wave"
            />
          </Grid>
        ))}
      </Box>
    );
  };
  // --- End Skeleton Loader ---
  
  return (
    <Box sx={{ ...commonContainerStyles, ...mobileContainerStyles }}>
      <Container maxWidth="lg" disableGutters>
        {showTitle && (
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ ...commonTitleStyles, ...mobileTitleStyles }}
          >
            {t('dashboard.brandsCarousel.title')}
          </Typography>
        )}
        
        {/* Hidden SEO description */}
        <Typography 
          component="p" 
          sx={commonSeoDescriptionStyles}
        >
          {t('dashboard.brandsCarousel.description')}
        </Typography>
        
        <Box sx={mobileCarouselContainerStyles}>
          {isLoading ? renderSkeletons() : (
            <Box 
              component={motion.div} // Use motion.div for animation
              sx={gridStyles}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {brands.map((brand) => (
                <Box 
                  component={motion.div} // Animate each item
                  key={brand.id} 
                  variants={itemVariants} 
                  // No need for sx here, BrandCard handles its styles
                >
                  <BrandCard 
                    brand={brand} 
                    deviceType="mobile"
                    sx={!useAlternativeLayout ? mobileVerticalBrandCardStyles : {}}
                    isCurrentBrand={currentBrandId === brand.id}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

MobileCarousel.propTypes = {
  brands: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired
    })
  ).isRequired,
  showTitle: PropTypes.bool,
  useAlternativeLayout: PropTypes.bool,
  currentBrandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

MobileCarousel.defaultProps = {
  showTitle: true,
  useAlternativeLayout: true,
  currentBrandId: null
};

export default memo(MobileCarousel);